import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import { SavedWording } from './DropSavedWording';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };
export const PUSH_SKIP_REASON = {
  limit: 'Push limit reached for now',
  too_soon: 'Too soon since the last Drop push',
  out_of_stock: 'Out of stock in this region',
  disabled: 'Drop pushes are off for this region',
  not_in_drop: 'Not in The Drop for this region',
};
const inputCls = 'w-full bg-[#050003] border border-[#2A0F3D] focus:border-[#FF9F0A] font-mono text-sm px-2 py-2 text-white outline-none';

// Editable Drop push preview. Closing without choosing sends nothing.
export function DropPushPreview({ target, onDone }) {
  const [checks, setChecks] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (!target) return;
    setChecks(null);
    Promise.all(target.regions.map((r) => axios.get(`${API_URL}/api/products/${target.productId}/drop/push-preview`, { ...opts, params: { region: r } }).then((x) => x.data)))
      .then((rows) => { setChecks(rows); setTitle(rows[0]?.title || ''); setBody(rows[0]?.body || ''); setPicked(null); })
      .catch(() => { toast.error('Could not load push preview'); onDone(); });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = (checks || []).filter((c) => c.status === 'ok').map((c) => c.region);
  const savedId = picked && picked.title === title && picked.body === body ? picked.id : null;
  const pick = (m) => { setTitle(m.title); setBody(m.body); setPicked(m); };
  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/products/${target.productId}/drop/push`, { regions: ready, title: title.trim(), body: body.trim(), saved_id: savedId }, opts);
      Object.entries(data.results).forEach(([r, st]) => (st === 'sent'
        ? toast.success(`Drop push sent · ${r.toUpperCase()}`)
        : toast.warning(`Drop push skipped · ${r.toUpperCase()}`, { description: PUSH_SKIP_REASON[st] || st })));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Push failed');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onDone(); }}>
      <DialogContent className="bg-[#0C0612] border-[#FF9F0A]/40 max-w-md" data-testid="drop-push-preview">
        <DialogHeader>
          <DialogTitle className="font-mono text-[#FF9F0A] flex items-center gap-2"><Bell className="w-4 h-4" /> SEND DROP PUSH?</DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-[#6B8E73]">The product is already in The Drop. This only decides the customer push.</DialogDescription>
        </DialogHeader>
        {!checks ? <p className="font-mono text-xs text-[#6B8E73]">Loading…</p> : (
          <div className="space-y-3 font-mono text-xs">
            <div className="space-y-1" data-testid="drop-push-status">
              {checks.map((c) => (
                <p key={c.region} className={c.status === 'ok' ? 'text-[#39FF14]' : 'text-[#FF9F0A]'} data-testid={`drop-push-status-${c.region}`}>
                  {c.region.toUpperCase()}: {c.status === 'ok' ? 'ready to send' : `will be skipped (${PUSH_SKIP_REASON[c.status] || c.status})`}
                </p>
              ))}
            </div>
            <SavedWording region={checks[0].region} productName={checks[0].product_name} initial={checks[0].saved_messages}
              title={title} body={body} activeId={savedId} onPick={pick} top={checks[0].top_saved} />
            <label className="block space-y-1 text-[#A3C7A8] uppercase text-[10px]">Title · {title.length}/60
              <input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="drop-push-title" />
            </label>
            <label className="block space-y-1 text-[#A3C7A8] uppercase text-[10px]">Message · {body.length}/180
              <textarea value={body} maxLength={180} rows={3} onChange={(e) => setBody(e.target.value)} className={`${inputCls} resize-none`} data-testid="drop-push-body" />
            </label>
            <div className="border border-[#2A0F3D] bg-[#050003] p-2" data-testid="drop-push-mock">
              <p className="text-white text-[12px] font-bold truncate">{title || '—'}</p>
              <p className="text-[#A3C7A8] text-[11px]">{body || '—'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={onDone} className="h-11 border border-[#6B8E73]/60 text-[#A3C7A8] uppercase tracking-wider hover:bg-white/5" data-testid="drop-push-skip">Skip</button>
              <button type="button" onClick={send} disabled={sending || !ready.length || !title.trim() || !body.trim()}
                className="h-11 bg-[#FF9F0A] text-[#050003] font-bold uppercase tracking-wider disabled:opacity-40" data-testid="drop-push-send">
                {sending ? 'Sending…' : 'Send push'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
